// Web component `wc-allineamento` (Fase 5 multi-tipo + solo_attivi).
//
// Mostra lo stato dell'allineamento per `(cassa, anno, mese)` letto da
// `/api/allineamenti/:cassa/stato`. Bottoni:
//   - "Annulla allineamento"  : visibile se `puoRevertire`.
//
// Il bottone "Allinea archivi" multi-tipo NON e' qui: e' esposto a livello
// riga nello storico elaborazioni (vedi `elaborazioni-storico.njk`).
//
// Mono-tipo `solo_attivi=1`: mostra solo info "Allineamento automatico ...".
// Mono-tipo `solo_attivi=0`: il componente NON va montato (decisione lato server).
//
// Attributi: data-cassa, data-anno, data-mese.
//
// Output minimale (DOM); rendering inline, niente template NJK separato.

import AppComponent from '../base/app-component.ts';

type AllineamentoInfo = {
  origine: 'auto' | 'manuale';
  eseguito_at: string;
  eseguito_da: string | null;
  n_cessate_implicite: number;
};

type StatoResponse = {
  applicabile: boolean;
  isMultiTipo: boolean;
  stato: 'fatto' | 'mai';
  allineamento: AllineamentoInfo | null;
  puoAllineare: boolean;
  motivoBlocco?: string;
  puoRevertire: boolean;
};

export class Allineamento extends AppComponent {
  static override componentName = 'wc-allineamento';

  private cassa = '';
  private anno = 0;
  private mese = 0;
  private multiTipo = false;

  /** Override per saltare il lookup template NJK: questo componente
   *  costruisce il suo HTML direttamente in `render()` da `refresh()`. */
  protected override render(): void {
    this.cassa = this.getAttribute('data-cassa') ?? '';
    this.anno = Number(this.getAttribute('data-anno') ?? '0');
    this.mese = Number(this.getAttribute('data-mese') ?? '0');
    // Default off; il backend conferma applicabilita' + multi/mono via `/stato`.
    this.innerHTML = '';
    void this.refresh();

    this.delegate.on('click', '[data-action="revert"]', (e: Event) => {
      e.preventDefault();
      void this.onRevert();
    });
  }

  private async refresh(): Promise<void> {
    if (!this.cassa || !this.anno || !this.mese) return;
    const r = await fetch(`/api/allineamenti/${this.cassa}/stato?anno=${this.anno}&mese=${this.mese}`, {
      credentials: 'same-origin',
    });
    if (!r.ok) {
      this.innerHTML = '';
      return;
    }
    const data = await r.json() as StatoResponse;
    if (!data.applicabile) {
      this.innerHTML = '';
      return;
    }
    this.multiTipo = data.isMultiTipo;
    this.renderState(data);
  }

  private renderState(s: StatoResponse): void {
    const info = s.allineamento;
    const infoLine = info
      ? `Allineamento ${info.origine === 'auto' ? 'automatico' : 'manuale'} eseguito ${this.formatDate(info.eseguito_at)} — ${info.n_cessate_implicite} cessate implicite${info.eseguito_da ? ` (${this.escape(info.eseguito_da)})` : ''}`
      : `Allineamento non ancora eseguito`;

    const azioni: string[] = [];
    if (this.multiTipo && s.puoRevertire) {
      azioni.push(
        `<button type="button" class="btn btn-sm btn-outline-warning" data-action="revert">Annulla allineamento</button>`,
      );
    }

    // Info "Allineamento bloccato" quando manuale, mai eseguito e bloccato da N-1.
    const blocco = this.multiTipo && s.stato === 'mai' && !s.puoAllineare && s.motivoBlocco
      ? `<span class="text-warning ms-2"><i class="bi bi-exclamation-circle"></i> ${this.escape(s.motivoBlocco)}</span>`
      : '';

    this.innerHTML = `
      <div class="d-flex align-items-center gap-2 small">
        <span class="text-muted">${this.escape(infoLine)}</span>
        ${blocco}
        ${azioni.length ? `<span class="ms-2">${azioni.join('')}</span>` : ''}
      </div>
    `;
  }

  private async onRevert(): Promise<void> {
    const ok = await Weext.confirm(
      `Annullare l'ultimo allineamento per il periodo ${String(this.mese).padStart(2, '0')}/${this.anno}? Le cessazioni implicite verranno rimosse.`,
      { title: 'Conferma annullamento', confirmText: 'Annulla allineamento', confirmClass: 'btn-warning' },
    );
    if (!ok) return;
    const r = await fetch(`/api/allineamenti/${this.cassa}/allinea/revert`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    const data = await r.json().catch(() => ({})) as { error?: string; nCessateRipristinate?: number };
    if (!r.ok) {
      Weext.toast(data.error ?? 'Errore annullamento', { type: 'danger' });
      return;
    }
    Weext.toast(`Allineamento annullato (${data.nCessateRipristinate ?? 0} cessate ripristinate)`, { type: 'success' });
    void this.refresh();
  }

  private formatDate(iso: string): string {
    if (!iso) return '';
    return iso.replace('T', ' ').slice(0, 16);
  }

  private escape(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
